import { PlacementId } from './placementIds'
import {
  AvailabilityMatrix,
  EMBROIDERY_TYPES,
  EmbroideryConstraintSet,
  EmbroideryTypeKey,
  PlacedEmbroideryItem,
  PlacementAvailability,
  PlacementRule,
  SelectionError,
  TypeAvailability,
  UnavailabilityReason,
  Violation,
} from './types'

/**
 * The embroidery combination-rules engine.
 *
 * This file contains no business rules. They live as reusable functions in `rules.ts`, configured
 * per flow in `flows/*.ts`, and arrive as `constraints.rules` — assembled at the flow's composition
 * root and handed to the provider stack at mount.
 *
 * The main entry point is `getAvailabilityMatrix(items, constraints)`: current selections in, and
 * for every type, whether it can still be added and at which placements — which is what the UI
 * needs to hide and disable. `violations()` is the commit guard underneath it, and availability is
 * derived from it by probing ("what would happen if I added this here?"). That derivation is
 * deliberate: the two can never drift, so the UI cannot offer something the guard would reject.
 * The one thing availability sets aside is a violation the selection already had, which belongs to
 * an existing item rather than to anything the customer is about to add — see `probeWith`.
 */

/**
 * The garment's placements keyed by id.
 *
 * The catalog lists placements per embroidery type — where a logo can go, where text can go — so
 * anatomy arrives with the same placement in it more than once, the chest for the logo and the
 * chest again for the text. Those entries are merged so a duplicate never drops the types its
 * twin allowed.
 */
const anatomyByPlacementId = (
  anatomy: readonly PlacementRule[]
): Map<PlacementId, PlacementRule> => {
  const byId = new Map<PlacementId, PlacementRule>()

  for (const placement of anatomy) {
    const seen = byId.get(placement.id)
    byId.set(
      placement.id,
      seen
        ? {
            id: placement.id,
            allowedTypes: [...new Set([...seen.allowedTypes, ...placement.allowedTypes])],
          }
        : placement
    )
  }

  return byId
}

/**
 * Could each item exist on this garment at all, ignoring what else is selected?
 *
 * Per item, in order: does the garment have that placement, does that placement accept that type,
 * and does any overlay narrow it further. The first failure wins — one violation per bad item, not
 * one per broken condition.
 */
const structuralViolations = (
  items: readonly PlacedEmbroideryItem[],
  constraints: EmbroideryConstraintSet
): Violation[] => {
  const anatomyById = anatomyByPlacementId(constraints.anatomy)

  return items.flatMap((item): Violation[] => {
    const at = {
      typeKey: item.typeKey,
      placementId: item.placementId,
      instanceIds: [item.instanceId],
    }

    const placement = anatomyById.get(item.placementId)
    if (!placement) return [{ reason: 'PLACEMENT_NOT_ON_GARMENT', ...at }]
    if (!placement.allowedTypes.includes(item.typeKey)) {
      return [{ reason: 'EMBROIDERY_TYPE_NOT_ALLOWED_AT_PLACEMENT', ...at }]
    }

    const allowedByOverlay = constraints.anatomyOverlay?.allowedPlacementsByType?.[item.typeKey]
    if (allowedByOverlay && !allowedByOverlay.includes(item.placementId)) {
      return [{ reason: 'BLOCKED_BY_OVERLAY', ...at }]
    }

    const lockedByOverlay = constraints.anatomyOverlay?.lockedPlacementByType?.[item.typeKey]
    if (lockedByOverlay && lockedByOverlay !== item.placementId) {
      return [{ reason: 'BLOCKED_BY_OVERLAY', ...at }]
    }

    return []
  })
}

/**
 * Every reason this selection is illegal. Empty = legal.
 */
export const violations = (
  items: readonly PlacedEmbroideryItem[],
  constraints: EmbroideryConstraintSet
): Violation[] => [
  // anatomy + overlay — does this garment physically allow the embroidery item
  ...structuralViolations(items, constraints),
  // the flow's combination rules, assembled in `flows/*.ts`
  ...constraints.rules.flatMap(check => check(items)),
]

const PROBE_INSTANCE_ID = '__probe__'

/** What makes two violations the same problem: the rule that fired and the items it names. */
const violationKey = (violation: Violation): string =>
  [
    violation.reason,
    violation.typeKey ?? '',
    violation.placementId ?? '',
    [...(violation.instanceIds ?? [])].sort().join(','),
  ].join('|')

/**
 * Hypothetically would this item be accepted if the customer added it here? An empty result means yes.
 *
 * Only violations the probe introduces are returned. A selection can already be illegal — items
 * held over from a garment that had sleeves, say — and counting those would make every probe come
 * back non-empty, locking the whole form behind a reason belonging to some other item. A rule that
 * was already firing and now names the probe too counts as introduced, which is what keeps a full
 * placement closed. The pre-existing violation itself is still reported by `validateSelectionSet`,
 * attributed to the item that actually caused it.
 */
const probeWith = (
  items: readonly PlacedEmbroideryItem[],
  typeKey: EmbroideryTypeKey,
  placementId: PlacementId,
  constraints: EmbroideryConstraintSet
): Violation[] => {
  const probe = { instanceId: PROBE_INSTANCE_ID, typeKey, placementId }
  const alreadyThere = new Map<string, number>()

  // Count what is already wrong with the selection, before the probe is added
  for (const violation of violations(items, constraints)) {
    const key = violationKey(violation)
    alreadyThere.set(key, (alreadyThere.get(key) ?? 0) + 1)
  }

  // Now check the selection with the probe in it, and keep only what the probe added
  return violations([...items, probe], constraints).filter(violation => {
    const key = violationKey(violation)
    const remaining = alreadyThere.get(key) ?? 0
    if (remaining === 0) return true
    alreadyThere.set(key, remaining - 1)
    return false
  })
}

/**
 * Placements this garment offers for a type, before considering what is already selected.
 *
 * Both overlay fields narrow, exactly as `structuralViolations` applies them: a placement has to
 * survive the allow-list and the lock. Treating either as the only word would offer a placement
 * the commit guard then rejects.
 */
const candidatePlacements = (
  typeKey: EmbroideryTypeKey,
  constraints: EmbroideryConstraintSet
): readonly PlacementRule[] => {
  const lockedByOverlay = constraints.anatomyOverlay?.lockedPlacementByType?.[typeKey]
  const allowedByOverlay = constraints.anatomyOverlay?.allowedPlacementsByType?.[typeKey]

  return [...anatomyByPlacementId(constraints.anatomy).values()].filter(placement => {
    if (!placement.allowedTypes.includes(typeKey)) return false
    if (allowedByOverlay && !allowedByOverlay.includes(placement.id)) return false
    if (lockedByOverlay && lockedByOverlay !== placement.id) return false
    return true
  })
}

/**
 * Placements where this type could be added right now. Pass `items` with an item *excluded* to get
 * the options for editing that item in place, so it doesn't block its own placement.
 */
export const availablePlacementsForType = (
  typeKey: EmbroideryTypeKey,
  items: readonly PlacedEmbroideryItem[],
  constraints: EmbroideryConstraintSet
): readonly PlacementRule[] =>
  candidatePlacements(typeKey, constraints).filter(
    placement => probeWith(items, typeKey, placement.id, constraints).length === 0
  )

/**
 * Whether another item of this type can be added anywhere.
 *
 * When it cannot, the reason comes from probing the candidate placements in order — and because a
 * flow's rulebook lists garment-wide caps first, a cap wins over a narrower placement rule.
 * `'NO_PLACEMENT'` means the garment offers nowhere to put it at all, once anatomy and any overlay
 * have been applied.
 */
export const canAddType = (
  typeKey: EmbroideryTypeKey,
  items: readonly PlacedEmbroideryItem[],
  constraints: EmbroideryConstraintSet
): { allowed: boolean; reason?: UnavailabilityReason } => {
  const candidates = candidatePlacements(typeKey, constraints)
  if (candidates.length === 0) return { allowed: false, reason: 'NO_PLACEMENT' }

  const openPlacement = candidates.find(
    placement => probeWith(items, typeKey, placement.id, constraints).length === 0
  )
  if (openPlacement) return { allowed: true }

  const [firstBlocked] = candidates.flatMap(placement =>
    probeWith(items, typeKey, placement.id, constraints)
  )
  return { allowed: false, reason: firstBlocked?.reason ?? 'NO_PLACEMENT' }
}

/**
 * For every offered type, whether it can be added and where. Memoize this once per selection
 * change rather than calling the probes per component.
 *
 * Every placement the garment has appears, so the UI needs no join against anatomy:
 * `offered: false` -> hide it; `offered: true, available: false` -> render it disabled, with `reason`.
 *
 * Per type, `offered` answers "render this form at all?" (structural — does the garment take a
 * logo anywhere) and `canAdd` answers "enable the add control?" (transient — is there room now).
 */
export const getAvailabilityMatrix = (
  items: readonly PlacedEmbroideryItem[],
  constraints: EmbroideryConstraintSet
): AvailabilityMatrix => {
  const matrix = {} as Record<EmbroideryTypeKey, TypeAvailability>

  for (const typeKey of EMBROIDERY_TYPES) {
    const offeredIds = new Set(
      candidatePlacements(typeKey, constraints).map(placement => placement.id)
    )
    const placements: PlacementAvailability[] = [
      ...anatomyByPlacementId(constraints.anatomy).values(),
    ].map(placement => {
      if (!offeredIds.has(placement.id)) {
        return {
          placementId: placement.id,
          offered: false,
          available: false,
          reason: placement.allowedTypes.includes(typeKey)
            ? 'BLOCKED_BY_OVERLAY'
            : 'EMBROIDERY_TYPE_NOT_ALLOWED_AT_PLACEMENT',
        }
      }
      const [firstViolation] = probeWith(items, typeKey, placement.id, constraints)
      return {
        placementId: placement.id,
        offered: true,
        available: firstViolation === undefined,
        reason: firstViolation?.reason,
        instanceIds: firstViolation?.instanceIds,
      }
    })

    const { allowed, reason } = canAddType(typeKey, items, constraints)
    matrix[typeKey] = {
      typeKey,
      offered: placements.some(placement => placement.offered),
      canAdd: allowed,
      reason,
      placements,
    }
  }

  return matrix
}

/**
 * Validates the embroidery form selections on submit. Normally empty, because the availabilityMatrix
 * would have prevented illegal selections from the form anyway. It also covers required types being absent.
 */
export const validateSelectionSet = (
  items: readonly PlacedEmbroideryItem[],
  constraints: EmbroideryConstraintSet
): SelectionError[] => {
  const missingRequired: SelectionError[] = (constraints.requiredTypes ?? [])
    .filter(typeKey => !items.some(item => item.typeKey === typeKey))
    .map(typeKey => ({ reason: 'MISSING_REQUIRED_TYPE', typeKey }))

  return [...violations(items, constraints), ...missingRequired]
}
